import { ReactElement, useCallback } from 'react';

import { PatientMedicine } from '@dreryk/egabinet-prescription-frontend-api';
import { VisitDiagnosis } from '@dreryk/egabinet-visit-frontend-api';

import { Reimbursement } from '~libs.shared.prescription';

import { MedicineOutdatedNotification } from '../MedicineOutdatedNotification/MedicineOutdatedNotification';
import { MedicineDetailsModalOpenAction } from '../MedicineDetailsModal/MedicineDetailsModalOpenAction';
import { MedicineName } from '../MedicineName';
import { MedicineActiveIngredients } from '../MedicineActiveIngredients';
import { MedicineReimbursementSelector } from '../MedicineReimbursementSelector/MedicineReimbursementSelector';
import { translationKeys } from '../../translations';

import { OnMedicineSelection } from './MedicineFullNameCell.types';

type MedicineFullNameCellProps = {
   readonly onMedicineSelection: OnMedicineSelection;
   readonly patientMedicine: PatientMedicine;
   readonly visitDiagnoses: VisitDiagnosis[];
};

export const MedicineFullNameCell = (props: MedicineFullNameCellProps): ReactElement => {
   const { medicine, reimbursements } = props.patientMedicine;

   const valid = medicine.valid !== false;

   // eslint-disable-next-line react-hooks/exhaustive-deps
   const handleMedicineSelect = useCallback((): void => props.onMedicineSelection(props.patientMedicine), [
      props.onMedicineSelection,
      props.patientMedicine,
   ]);

   const handleMedicineSelectWithReimbursement = useCallback(
      (reimbursement: Reimbursement) => {
         const eligibleIcd10CodesSet = new Set(
            reimbursement.reimbursementIndication?.map((indication) => indication.eligibleIcd10Codes).flat() ?? []
         );
         props.onMedicineSelection(props.patientMedicine, {
            ...reimbursement,
            eligibleIcd10Codes: Array.from(eligibleIcd10CodesSet),
         });
      },
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [props.onMedicineSelection, props.patientMedicine]
   );

   return (
      <div>
         {valid ? null : <MedicineOutdatedNotification />}
         <MedicineDetailsModalOpenAction
            confirmationButtonLabel={translationKeys.medicine.details.addToPrescription}
            medicine={medicine}
            reimbursements={reimbursements}
            onConfirm={handleMedicineSelect}
            onReimbursementClick={handleMedicineSelectWithReimbursement}
            visitDiagnoses={props.visitDiagnoses}
            render={(openModal) => (
               <MedicineName name={medicine.fullName} atc={medicine.atc} onClick={openModal} valid={valid} />
            )}
         />
         <MedicineActiveIngredients activeIngredients={medicine.activeIngredients} valid={valid} />
         {valid ? (
            <MedicineReimbursementSelector
               reimbursements={reimbursements}
               onReimbursementClick={handleMedicineSelectWithReimbursement}
               disabled={!valid}
               visitDiagnoses={props.visitDiagnoses}
            />
         ) : null}
      </div>
   );
};
